import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import Select from "react-select";

import { fetchVendorProducts } from "../redux/slices/productSlice";
import api from "../services/api";

const CreateBillByVendor = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { user } = useSelector((state) => state.auth);
    const vendorId = user?.vendor?._id;

    const { vendorProducts, loading } = useSelector(
        (state) => state.product
    );

    const [customerName, setCustomerName] = useState("");
    const [customerMobile, setCustomerMobile] = useState("");
    const [customerAddress, setCustomerAddress] = useState("");

    const [selectedProducts, setSelectedProducts] = useState([]);
    const [items, setItems] = useState([]);

    const [paymentMode, setPaymentMode] = useState("Cash");

    // 🔥 GST state
    const [taxPercent, setTaxPercent] = useState(0);

    // Fetch vendor products
    useEffect(() => {
        if (vendorId) {
            dispatch(fetchVendorProducts({ vendorId, params: { page: 1, limit: 1000 } }));
        }
    }, [dispatch, vendorId]);

    // When selectedProducts change, sync items
    useEffect(() => {
        const newItems = selectedProducts.map((p) => {
            const existing = items.find((i) => i.product === p.value);
            return (
                existing || {
                    product: p.value,
                    name: p.label,
                    price: p.price,
                    quantity: 1,
                    total: p.price,
                }
            );
        });
        setItems(newItems);
        // eslint-disable-next-line
    }, [selectedProducts]);

    const handleQuantityChange = (index, value) => {
        const updated = [...items];
        updated[index].quantity = Number(value);
        updated[index].total = updated[index].price * updated[index].quantity;
        setItems(updated);
    };

    // ✅ Calculations
    const subTotal = items.reduce((acc, i) => acc + i.total, 0);
    const taxAmount = (subTotal * taxPercent) / 100;
    const grandTotal = subTotal + taxAmount;

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!items.length) {
            Swal.fire("Warning", "Please select at least one product", "warning");
            return;
        }

        const confirm = await Swal.fire({
            title: "Create Bill?",
            text: "Are you sure you want to generate this bill?",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Yes, create",
        });

        if (!confirm.isConfirmed) return;

        const payload = {
            vendor: vendorId,
            customerName,
            customerMobile,
            customerAddress,
            items,
            paymentMode,
            taxPercent,
        };

        try {
            await api.post("/invoice/create-bill", payload);
            Swal.fire("Success", "Bill created successfully", "success");
            navigate("/my-bills");
        } catch (err) {
            Swal.fire(
                "Error",
                err.response?.data?.message || "Failed to create bill",
                "error"
            );
        }
    };

    const productOptions = vendorProducts.map((p) => ({
        value: p._id,
        label: p.name,
        price: p.price,
    }));

    return (
        <div className="card">
            <div className="card-header">
                <h6>Create Bill</h6>
            </div>

            <div className="card-body">
                <form onSubmit={handleSubmit}>
                    {/* Customer Details */}
                    <div className="mb-2">
                        <label className="form-label">Customer Name</label>
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            value={customerName}
                            onChange={(e) => setCustomerName(e.target.value)}
                        />
                    </div>

                    <div className="mb-2">
                        <label className="form-label">Customer Mobile</label>
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            value={customerMobile}
                            onChange={(e) => setCustomerMobile(e.target.value)}
                        />
                    </div>

                    <div className="mb-2">
                        <label className="form-label">Customer Address</label>
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            value={customerAddress}
                            onChange={(e) => setCustomerAddress(e.target.value)}
                        />
                    </div>

                    {/* Product Selection */}
                    <div className="mb-2">
                        <label className="form-label">Select Products</label>
                        <Select
                            isMulti
                            options={productOptions}
                            value={selectedProducts}
                            onChange={setSelectedProducts}
                            isLoading={loading}
                            placeholder="Search & select products"
                        />
                    </div>

                    {/* Items Table */}
                    {items.length > 0 && (
                        <>
                            <h6>Items</h6>
                            <table className="table table-sm table-bordered mb-3">
                                <thead>
                                    <tr>
                                        <th>Product</th>
                                        <th>Price</th>
                                        <th>Quantity</th>
                                        <th>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((item, idx) => (
                                        <tr key={idx}>
                                            <td>{item.name}</td>
                                            <td>{item.price}</td>
                                            <td>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    className="form-control form-control-sm"
                                                    value={item.quantity}
                                                    onChange={(e) =>
                                                        handleQuantityChange(idx, e.target.value)
                                                    }
                                                />
                                            </td>
                                            <td>{item.total}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </>
                    )}

                    {/* GST Dropdown */}
                    <div className="mb-2">
                        <label className="form-label">GST (%)</label>
                        <select
                            className="form-select form-select-sm"
                            value={taxPercent}
                            onChange={(e) => setTaxPercent(Number(e.target.value))}
                        >
                            <option value={0}>No GST</option>
                            <option value={5}>5%</option>
                            <option value={9}>9%</option>
                            <option value={18}>18%</option>
                            <option value={28}>28%</option>
                        </select>
                    </div>

                    {/* Summary */}
                    <div className="mb-2">
                        <small>Sub Total: ₹{subTotal.toFixed(2)}</small><br />
                        <small>GST: ₹{taxAmount.toFixed(2)}</small><br />
                        <strong>Grand Total: ₹{grandTotal.toFixed(2)}</strong>
                    </div>

                    {/* Payment Mode */}
                    <div className="mb-2">
                        <label className="form-label">Payment Mode</label>
                        <select
                            className="form-select form-select-sm"
                            value={paymentMode}
                            onChange={(e) => setPaymentMode(e.target.value)}
                        >
                            <option value="Cash">Cash</option>
                            <option value="UPI">UPI</option>
                            <option value="Card">Card</option>
                        </select>
                    </div>

                    <div className="d-flex justify-content-end">
                        <button type="submit" className="btn btn-primary btn-sm">
                            Create Bill
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateBillByVendor;
